import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Bot, Code, Target, TrendingUp } from 'lucide-react';

const TeamPage = () => {
  const values = [
    {
      title: 'Future-First',
      description: 'We embrace emerging technology and trends, ensuring our clients stay ahead of the curve.',
    },
    {
      title: 'Results-Driven',
      description: 'Every project is measured by its impact. We create work that delivers tangible outcomes.',
    },
    {
      title: 'Authentically Bold',
      description: 'We push boundaries while staying true to each brand\'s unique voice and values.',
    },
    {
      title: 'Relentlessly Curious',
      description: 'We never stop learning, experimenting, and finding better ways to solve problems.',
    },
  ];

  const team = [
    {
      name: 'Jon Hawkins',
      role: 'Founder & Director',
      image: '/images/jon-hawkins.jpg',
      bio: 'Digital transformation consultant with 15+ years navigating brands into the future using AI tools most humans won\'t believe exist.',
      featured: true,
    },
    {
      name: 'Paul',
      role: 'AI Assistant',
      icon: Bot,
      description: 'Our AI-powered research and strategy partner',
    },
    {
      name: 'Ziggy',
      role: 'AI Coder',
      icon: Code,
      description: 'Development and technical implementation',
    },
    {
      name: 'Tav',
      role: 'Strategic Advisor',
      icon: Target,
      description: 'Brand strategy and business growth',
    },
    {
      name: 'Ash',
      role: 'Marketing Lead',
      icon: TrendingUp,
      description: 'Campaigns and content strategy',
    },
  ];

  return (
    <div data-testid="team-page">
      {/* Hero - NO PHOTO, just text */}
      <section className="min-h-[50vh] flex items-center relative overflow-hidden bg-elate-surface">
        <div className="container-custom relative z-10 pt-32 pb-20">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            The Team
          </motion.p>

          <motion.h1
            className="text-headline text-white mb-8"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Humans & AI
            <br />
            <span className="text-elate-yellow">Combined</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Elate Global has been empowering brands for over 15 years. When AI emerged as a transformative force, we pivoted all our efforts to stay at the forefront—combining decades of brand expertise with cutting-edge technology.
          </motion.p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-elate-yellow">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-elate-black/60 uppercase tracking-widest text-sm mb-6">Our Mission</p>
            <h2 className="text-3xl md:text-4xl font-heading uppercase text-elate-black mb-8 leading-tight">
              Empowering Brands in a New Era
            </h2>
            <p className="text-xl text-elate-black/80 leading-relaxed mb-6">
              We set out to empower brands through digital media—websites, social content, and brand perception. We built a reputation for amplifying diverse brands and personalities through creative content and modern media strategies.
            </p>
            <p className="text-xl text-elate-black/80 leading-relaxed mb-6">
              We never imagined a world where we could hold all of the tools to empower brands in ways previously impossible. But that world is here. AI has transformed everything.
            </p>
            <p className="text-xl text-elate-black/80 leading-relaxed">
              Today, we don't just build websites and run campaigns. We develop in-house AI systems, automate lead generation, streamline operations, and help brands navigate into a future most people won't believe exists. This is the new generation of business empowerment—and we're leading the charge.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-padding bg-elate-black">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">Leadership</p>
            <h2 className="text-subhead text-white">
              Meet the Director
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/images/jon-hawkins.jpg"
                  alt="Jon Hawkins"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-elate-yellow p-6">
                <p className="text-elate-black font-heading text-2xl uppercase">Jon Hawkins</p>
                <p className="text-elate-black/70 text-sm uppercase tracking-wider">Founder & Director</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <blockquote className="text-2xl text-white font-heading uppercase mb-8 leading-tight">
                "Navigating brands into the future working with AI tools most humans won't believe exist. Don't fall behind the competition."
              </blockquote>

              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  With a career spanning award-winning law firms, investment funds, racing drivers, boxing world champions and even the Teletubbies, Jon has built a reputation for amplifying diverse brands through creative content and modern media strategies.
                </p>
                <p>
                  Jon spent over 15 years performing on stage in iconic productions like Mamma Mia! and The Rocky Horror Show, honing storytelling skills that laid the foundation for his shift into digital marketing.
                </p>
                <p>
                  In 2008, Jon founded Elate Global, which has since become a leader in creating innovative strategies for clients across industries. As a Digital Transformation Consultant, he uses his expertise in software and AI tools to help companies streamline operations and enhance brand presence.
                </p>
                <p>
                  As of 2026, Jon is innovating with <span className="text-elate-yellow">app creation</span> and building <span className="text-elate-yellow">AI tech stacks</span> that help businesses automate and optimise their processes, delivering results while saving time and resources.
                </p>
                <p>
                  Jon also co-owns Footprint Family Ltd, a production company reshaping entertainment for young audiences. Their flagship IP <span className="text-elate-yellow">Sing-A-Saurus</span> has garnered over 1 million views on Prime Video, while <span className="text-elate-yellow">The Lullaby Bear</span> has amassed 100 million streams across socials.
                </p>
              </div>

              <div className="mt-8">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Drop Me a Message
                  <ArrowUpRight size={20} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-elate-surface">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">The Wider Team</p>
            <h2 className="text-subhead text-white">
              Humans & AI Working Together
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {team.filter(m => !m.featured).map((member, index) => (
              <motion.div
                key={member.name}
                className="bg-elate-black border border-white/10 p-6 text-center hover:border-elate-yellow/50 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-elate-yellow/10 rounded-full flex items-center justify-center">
                  {member.icon && <member.icon className="w-8 h-8 text-elate-yellow" />}
                </div>
                <h4 className="text-xl font-heading uppercase text-white mb-1">
                  {member.name}
                </h4>
                <p className="text-elate-yellow text-sm uppercase tracking-wider mb-2">
                  {member.role}
                </p>
                <p className="text-gray-500 text-sm">
                  {member.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="caption text-elate-yellow mb-4">Our Values</p>
            <h2 className="text-subhead text-white">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="border-l-2 border-elate-yellow pl-8 py-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <h3 className="text-2xl font-heading uppercase text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 border-y border-white/10 bg-elate-surface">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '15+', label: 'Years Experience' },
              { number: '50+', label: 'Projects Delivered' },
              { number: '4', label: 'Specialist Sectors' },
              { number: '100M+', label: 'Content Streams' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <p className="text-5xl font-heading text-elate-yellow mb-2">
                  {stat.number}
                </p>
                <p className="text-gray-400 text-sm uppercase tracking-wider">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            className="bg-elate-yellow p-12 md:p-20 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-subhead text-elate-black mb-6">
              Let's Work Together
            </h2>
            <p className="text-elate-black/70 max-w-xl mx-auto mb-8 text-lg">
              Ready to build something extraordinary? We'd love to hear about your project and explore how we can empower your brand with AI.
            </p>
            <Link
              to="/contact"
              className="bg-elate-black text-white font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center gap-2 hover:bg-white hover:text-elate-black transition-colors"
            >
              Get in Touch
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
